package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.kyd.util.Vector2

/**
 * When a mouse event needs to check if the mouse is "touching" an actor,
 * how is "touching" defined.
 *
 * For some actors, this may be a simple bounds-check (i.e. is the mouse inside the actor's bounding box).
 * See [WithinBounds].
 *
 * For others, we may want to do pixel detection (i.e. does the actor's appearance draw an opaque pixel at
 * this point). See [TouchingPixel].
 *
 * For multi-line labels, we don't want pixel detection, because that would be too finicky.
 * But just checking the overall bounds may give false positives.
 * A better approach would check the bounding box of each _line_ of the Label's text.
 * FYI. At time of writing, this doesn't exist!
 *
 * Should we include the actor's children too? See [TouchingChildren].
 */
interface Touching {

    fun touching(actor: Actor, localPoint: Vector2): Boolean

}

/**
 * Performs 2 [Touching] tests. If the second test is only carried out if the first test passes.
 */
class TouchingAnd(val a: Touching, val b: Touching) : Touching {

    override fun touching(actor: Actor, localPoint: Vector2): Boolean {
        if (! a.touching(actor, localPoint)) return false
        return b.touching(actor, localPoint)
    }

}

object WithinBounds : Touching {

    override fun touching(actor: Actor, localPoint: Vector2): Boolean {
        return actor.appearance.containsLocalPoint(localPoint)
    }

}

/**
 * Returns true iff the pixel at the given point has a alpha value > [alphaThreshold].
 */
class TouchingPixel(val alphaThreshold: Float) : Touching {

    override fun touching(actor: Actor, localPoint: Vector2): Boolean {
        // TODO Inefficient - transforming back to world coordinates.
        val worldPoint = localPoint * actor.localToWorld
        return actor.colorAtWorldPoint(worldPoint).alpha > alphaThreshold
    }

    companion object {
        /**
         * Threshold = 0.01f
         */
        val instance = TouchingPixel(0.01f)

        /**
         * First check the point is within the appearance's bounds.
         * If it is, then use [TouchingPixel.instance].
         *
         * Assuming that the bounds checking is more efficient than the pixel detection,
         * then this will be quicker than just checking pixels.
         */
        val withinBoundsAndPixel = TouchingAnd(WithinBounds, instance)
    }

}

/**
 * [Touching] test which also checks the actor's children.
 *
 * [baseTest] could be a simple implementation of [Touching], such as [WithinBounds] or [TouchingPixel],
 * but it could also be [TouchingChildren] if we want to test the grandchildren too.
 * However, if you want to test _all_ descendants, then use [TouchingDescendants]
 */
class TouchingChildren(val baseTest: Touching) : Touching {

    override fun touching(actor: Actor, localPoint: Vector2): Boolean {
        if (baseTest.touching(actor, localPoint)) return true
        for (child in actor.children) {
            val childLocal = localPoint * child.worldToLocal
            if (baseTest.touching(child, childLocal)) return true
        }
        return false
    }

}

/**
 * Checks if an actor or any of its descendant actors are touching the given point.
 */
class TouchingDescendants(val baseTest: Touching) : Touching {

    override fun touching(actor: Actor, localPoint: Vector2): Boolean {

        fun testOne(actor: Actor, localPoint: Vector2): Boolean {
            if (baseTest.touching(actor, localPoint)) return true
            for (child in actor.children) {
                val childLocal = localPoint * child.worldToLocal
                if (testOne(child, childLocal)) return true
            }
            return false
        }

        return testOne(actor, localPoint)
    }

}
